import React from "react";
import type { ReactNode } from "react";
import type { ConsultNoteFlash, ConsultNoteSourceRef } from "./types";
import { flashClasses, flashMessage } from "./utils";

export function FlashBanner({ flash }: { flash: ConsultNoteFlash | null }) {
  if (!flashMessage(flash)) return null;

  return <section className={flashClasses(flash)}>{flashMessage(flash)}</section>;
}

export function SectionCardHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-neutral-950 dark:text-white">{title}</h2>
      </div>
      {action ? <div className="flex shrink-0 items-center justify-end">{action}</div> : null}
    </div>
  );
}

export function SourceTable({ sourceRefs }: { sourceRefs: ConsultNoteSourceRef[] }) {
  if (sourceRefs.length === 0) return null;

  return (
    <details className="group mt-4 overflow-hidden rounded-xl border border-neutral-200/80 dark:border-neutral-800">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-neutral-50/80 px-3 py-2.5 marker:hidden transition hover:bg-neutral-100/90 dark:bg-neutral-900/50 dark:hover:bg-neutral-900/80 [&::-webkit-details-marker]:hidden">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Sources</h3>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {sourceRefs.length} source ref{sourceRefs.length === 1 ? "" : "s"}
          </p>
        </div>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 group-open:text-emerald-800 dark:text-emerald-300 dark:group-open:text-emerald-200">
          Expand
          <span className="inline-flex transition-transform duration-200 group-open:rotate-180">⌄</span>
        </span>
      </summary>

      <div className="overflow-x-auto border-t border-neutral-200/80 dark:border-neutral-800">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
            <tr>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Value Used
              </th>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Source
              </th>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Open
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {sourceRefs.map((sourceRef) => (
              <tr key={`${sourceRef.label}-${sourceRef.value}-${sourceRef.openUrl || "none"}`}>
                <td className="px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200">{sourceRef.value}</td>
                <td className="px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200">{sourceRef.label}</td>
                <td className="px-3 py-2 text-sm">
                  {sourceRef.openUrl ? (
                    <a
                      href={sourceRef.openUrl}
                      className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open source
                    </a>
                  ) : (
                    <span className="text-neutral-400 dark:text-neutral-500">Unavailable</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
